import { api } from './api';
import { dedupedFetch, invalidateCache } from './cache';
import type { SelecaoEtapa3, SelecaoEtapa3Input, RascunhoEtapa3, RascunhoEtapa3Input } from 'types/etapa3';

const PATH_SELECAO = '/database/selecao_etapa3__c';

const PATH_RASCUNHO = '/database/rascunho_etapa3__c';

const selecaoAtivaKey = (userId: string, categoria: string) =>
  `selecao-etapa3-${userId}-${categoria}`;
const todasSelecoesKey = (userId: string) =>
  `selecao-etapa3-todas-${userId}`;
const rascunhoAtivoKey = (userId: string) =>
  `rascunho-etapa3-${userId}`;

// ─── Votos finais ─────────────────────────────────────────────────────────────

const fetchSelecaoAtivaRaw = async (
  // userId é derivado de context.player no servidor; mantido só p/ a chave de cache.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  userId: string,
  categoria: string
): Promise<SelecaoEtapa3 | null> => {
  const { data } = await api.post('/find/selecao_etapa3_ativa', { categoria });
  const results = Array.isArray(data) ? data : [];
  return results.length > 0 ? (results[0] as SelecaoEtapa3) : null;
};

export const fetchSelecaoAtivaEtapa3 = (
  userId: string,
  categoria: string
): Promise<SelecaoEtapa3 | null> =>
  dedupedFetch(
    selecaoAtivaKey(userId, categoria),
    () => fetchSelecaoAtivaRaw(userId, categoria),
    30_000
  );

export const fetchTodasSelecoesByUser = (
  userId: string
): Promise<SelecaoEtapa3[]> =>
  dedupedFetch(
    todasSelecoesKey(userId),
    async () => {
      // PARAM: também usado cross-usuário (assessor consulta os votos do VP).
      const { data } = await api.post('/find/selecoes_etapa3_by_user', { userId });
      return Array.isArray(data) ? (data as SelecaoEtapa3[]) : [];
    },
    30_000
  );

export const submitSelecaoEtapa3 = async (
  payload: SelecaoEtapa3Input
): Promise<SelecaoEtapa3> => {
  const previous = await fetchSelecaoAtivaRaw(payload.userId, payload.categoria);
  if (previous) {
    await api.put(PATH_SELECAO, { ...previous, supersededAt: Date.now() });
  }
  const { data } = await api.post(PATH_SELECAO, payload);
  invalidarCacheSelecaoEtapa3(payload.userId, payload.categoria);
  return data as SelecaoEtapa3;
};

export const invalidarCacheSelecaoEtapa3 = (
  userId: string,
  categoria?: string
): void => {
  invalidateCache(todasSelecoesKey(userId));
  if (categoria) invalidateCache(selecaoAtivaKey(userId, categoria));
};

// ─── Rascunho ─────────────────────────────────────────────────────────────────

const fetchRascunhoAtivoRaw = async (
  // userId é derivado de context.player no servidor; mantido só p/ a chave de cache.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  userId: string
): Promise<RascunhoEtapa3 | null> => {
  const { data } = await api.post('/find/rascunho_etapa3_ativo', {});
  const results = Array.isArray(data) ? data : [];
  return results.length > 0 ? (results[0] as RascunhoEtapa3) : null;
};

export const fetchRascunhoAtivo = (
  userId: string
): Promise<RascunhoEtapa3 | null> =>
  dedupedFetch(rascunhoAtivoKey(userId), () => fetchRascunhoAtivoRaw(userId), 15_000);

export const salvarRascunho = async (
  userId: string,
  notasRascunho: RascunhoEtapa3['notasRascunho']
): Promise<RascunhoEtapa3> => {
  const previous = await fetchRascunhoAtivoRaw(userId);
  if (previous) {
    await api.put(PATH_RASCUNHO, { ...previous, supersededAt: Date.now() });
  }
  const payload: RascunhoEtapa3Input = { userId, notasRascunho, atualizadoEm: Date.now() };
  const { data } = await api.post(PATH_RASCUNHO, payload);
  invalidateCache(rascunhoAtivoKey(userId));
  return data as RascunhoEtapa3;
};

export const deletarRascunhoAtivo = async (userId: string): Promise<void> => {
  const rascunho = await fetchRascunhoAtivoRaw(userId);
  if (!rascunho) return;
  await api.put(PATH_RASCUNHO, { ...rascunho, supersededAt: Date.now() });
  invalidateCache(rascunhoAtivoKey(userId));
};
